import React, { useState, useEffect } from "react";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader2, LogOut, ShieldCheck, CalendarClock, Lock } from "lucide-react";
import UpgradePlanDialog from "@/components/UpgradePlanDialog";

/**
 * TrialPaywall — soft paywall that appears when a tenant's 3-day trial has
 * expired. The console content stays mounted underneath (blurred + non-
 * interactive) so the user can see their channels are still there — they just
 * can't touch them until they upgrade.
 *
 * Not dismissible. Escape hatches are: (a) upgrade, (b) log out.
 */
export default function TrialPaywall({ tenant, onUpgraded }) {
  const { logout, refresh } = useAuth();
  const nav = useNavigate();
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [checking, setChecking] = useState(false);

  // If a payment just completed on another tab/window, the tenant should have
  // flipped off trial state — poll gently while the paywall is up so the user
  // isn't stuck if the webhook lands after their redirect.
  useEffect(() => {
    const iv = setInterval(async () => {
      try {
        const r = await api.get("/tenants/current");
        if (r.data && !r.data.is_trial) {
          clearInterval(iv);
          await refresh();
          toast.success("Subscription active — welcome back on air.");
          onUpgraded?.();
        }
      } catch { /* ignore */ }
    }, 8000);
    return () => clearInterval(iv);
  }, [refresh, onUpgraded]);

  const manuallyRefresh = async () => {
    setChecking(true);
    try {
      await refresh();
    } finally {
      setChecking(false);
    }
  };

  const channelCount = tenant?.trial_stats?.channels ?? "your";
  const brandName = tenant?.name || "your workspace";
  const trialEnded = tenant?.trial_ends_at
    ? new Date(tenant.trial_ends_at).toLocaleString(undefined, {
        month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
      })
    : "recently";

  return (
    <>
      {/* Backdrop covers the whole app */}
      <div
        data-testid="trial-paywall-backdrop"
        className="fixed inset-0 z-[200] bg-background/60 backdrop-blur-xl overflow-y-auto"
        style={{ WebkitBackdropFilter: "blur(24px)" }}
      >
        {/* Aurora blobs to match the auth aesthetic */}
        <div className="fixed -top-40 -left-40 w-[560px] h-[560px] rounded-full opacity-30 pointer-events-none" style={{ background: "radial-gradient(circle, rgba(251,113,133,0.55), transparent 65%)" }} />
        <div className="fixed -bottom-40 -right-40 w-[620px] h-[620px] rounded-full opacity-30 pointer-events-none" style={{ background: "radial-gradient(circle, rgba(139,92,246,0.55), transparent 65%)" }} />

        <div className="min-h-screen flex items-center justify-center p-6">
          <div
            className="glass-card w-full max-w-xl p-8 sm:p-10 relative"
            data-testid="trial-paywall-card"
          >
            <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[10px] uppercase tracking-widest font-mono border" style={{ borderColor: "rgba(251,113,133,0.4)", color: "#fb7185", background: "rgba(251,113,133,0.08)" }}>
              <Lock className="w-3 h-3" /> Trial expired
            </div>
            <h1 className="mt-5 font-display font-black text-4xl leading-[1.05] tracking-tighter">
              Your channels are safe.
              <br />
              <span className="text-gradient">Just resubscribe to keep them on air.</span>
            </h1>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              Your 3-day sandbox for <span className="text-foreground font-semibold">{brandName}</span> ended {trialEnded}.
              Everything you built — playlists, graphics layers, EPG entries, {typeof channelCount === "number" ? `${channelCount} channels` : "your channels"} —
              is preserved. Pick a plan and you&apos;ll be back live in under 60 seconds.
            </p>

            <ul className="mt-6 space-y-2.5 text-sm">
              {[
                ["Nothing was deleted. Your assets, EPG and graphics timelines are intact."],
                ["Sub-second reactivation — no rebuild, no re-upload."],
                ["Downgrade or cancel any time from the Stripe customer portal."],
              ].map(([t]) => (
                <li key={t} className="flex items-start gap-3">
                  <ShieldCheck className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" strokeWidth={1.75} />
                  <span className="text-foreground/85">{t}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button
                onClick={() => setUpgradeOpen(true)}
                data-testid="paywall-upgrade-btn"
                className="tenant-btn rounded-full h-11 px-6 font-display font-bold text-sm flex-1"
              >
                Pick a plan &amp; go live →
              </Button>
              <Button
                onClick={manuallyRefresh}
                variant="outline"
                disabled={checking}
                data-testid="paywall-refresh-btn"
                className="rounded-full h-11 px-4 gradient-stroke font-display font-bold text-sm"
              >
                {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : "I already paid — refresh"}
              </Button>
            </div>

            <div className="mt-6 flex items-center justify-between text-xs text-muted-foreground border-t border-border/50 pt-4">
              <div className="flex items-center gap-2 font-mono">
                <CalendarClock className="w-3.5 h-3.5" />
                Trial ended · {trialEnded}
              </div>
              <button
                onClick={logout}
                data-testid="paywall-logout-btn"
                className="inline-flex items-center gap-1.5 hover:text-foreground transition-colors"
              >
                <LogOut className="w-3.5 h-3.5" /> Sign out
              </button>
            </div>

            <div className="mt-4 text-[10px] font-mono uppercase tracking-widest text-muted-foreground text-center">
              Enterprise buyer?{" "}
              <button
                onClick={() => nav("/pricing")}
                data-testid="paywall-enterprise-btn"
                className="underline hover:text-foreground"
              >
                Talk to sales instead
              </button>
            </div>
          </div>
        </div>
      </div>

      <UpgradePlanDialog open={upgradeOpen} onClose={() => setUpgradeOpen(false)} />
    </>
  );
}
